import type {
  HarnessConversation,
  HarnessRun,
} from '@borradh-workspace/api-client/types';
import {
  Container,
  Database,
  FlaskConical,
  History,
  KeyRound,
  Laptop,
  MessageSquarePlus,
  Radio,
  RotateCcw,
  ShieldCheck,
} from 'lucide-react';
import { useState } from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

import {
  useHarnessRun,
  useHarnessRuns,
  useHarnessStatus,
  useImportLiveConversation,
} from '../../api';
import { HarnessRunView, ModeBadge, STATUS_META } from './harness-result';
import { LiveTrafficPanel } from './live-traffic-panel';
import { PastCasesPanel } from './past-cases-panel';
import { ScenariosPanel } from './scenarios-panel';
import { TryMessagePanel } from './try-message-panel';

function StatusPill({
  ok,
  icon: Icon,
  label,
}: {
  ok: boolean;
  icon: typeof Laptop;
  label: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs',
        ok
          ? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
          : 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300'
      )}
    >
      <Icon className="size-3.5" />
      {label}
    </span>
  );
}

function runSummary(run: HarnessRun) {
  if (run.status === 'running') return 'Running…';
  if (run.status === 'failed') return 'Failed';
  if (run.status === 'cancelled') return 'Stopped';
  const passed = run.items.filter((item) => item.status === 'pass').length;
  const failed = run.items.filter(
    (item) => item.status === 'fail' || item.status === 'error'
  ).length;
  if (run.kind === 'message') {
    const item = run.items[0];
    return item ? STATUS_META[item.status].label : 'No result';
  }
  // Live replays produce new replies to compare, not pass/fail verdicts.
  if (run.mode === 'live') {
    const replied = run.items.filter((item) => item.status === 'reply').length;
    return failed
      ? `${replied} replied, ${failed} failed`
      : `${replied} replied`;
  }
  return failed ? `${failed} failed` : `${passed}/${run.total} passed`;
}

function RecentRuns({
  runs,
  selectedId,
  onSelect,
}: {
  runs: HarnessRun[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  if (!runs.length) return null;
  return (
    <div className="space-y-2">
      <p className="flex items-center gap-1.5 font-medium text-muted-foreground text-xs uppercase tracking-wide">
        <History className="size-3.5" />
        Recent runs
      </p>
      <ul className="space-y-1">
        {runs.slice(0, 8).map((run) => (
          <li key={run.id}>
            <button
              type="button"
              onClick={() => onSelect(run.id)}
              className={cn(
                'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-muted',
                selectedId === run.id && 'bg-muted'
              )}
            >
              <ModeBadge mode={run.mode} />
              <span className="min-w-0 flex-1 truncate">{run.label}</span>
              <span className="shrink-0 text-muted-foreground text-xs">
                {runSummary(run)}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Needs the Harness to be reachable: hosted, or a local API. */
function HarnessUnavailable() {
  return (
    <Empty className="mt-16">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <Laptop />
        </EmptyMedia>
        <EmptyTitle>The Harness isn’t set up on this API</EmptyTitle>
        <EmptyDescription>
          Test chats and replays need the hosted Harness database, or an API
          running locally against a local database. Live traffic works
          everywhere.
        </EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
}

/** Talk to Claire as a customer, in the same inbox as live traffic. */
function TestChatWorkspace({
  openConversation,
}: {
  openConversation: HarnessConversation | null;
}) {
  const { status, isLoading, error } = useHarnessStatus();
  if (error) return <HarnessUnavailable />;
  if (isLoading || !status) return <Skeleton className="h-[560px] w-full" />;
  return (
    <div className="space-y-4">
      {status.available ? null : (
        <Alert variant="destructive">
          <Container className="size-4" />
          <AlertTitle>Can’t run yet</AlertTitle>
          <AlertDescription>{status.reason}</AlertDescription>
        </Alert>
      )}
      <TryMessagePanel
        hosted={status.mode === 'hosted'}
        disabled={!status.available}
        liveUnavailableReason={
          status.openAiKey
            ? null
            : 'Set OPENAI_API_KEY in the API’s .env to use live AI'
        }
        openConversation={openConversation}
      />
    </div>
  );
}

/** Replays in throwaway databases, or on the hosted Harness database. */
function ReplayWorkspace() {
  const { status, isLoading, error } = useHarnessStatus();
  const { runs } = useHarnessRuns();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const activeId = selectedId ?? runs[0]?.id ?? null;
  const { run } = useHarnessRun(activeId);

  if (error) return <HarnessUnavailable />;

  const hosted = status?.mode === 'hosted';
  const disabled = !status?.available;
  const liveUnavailableReason = status?.openAiKey
    ? null
    : 'Set OPENAI_API_KEY in the API’s .env to use live AI';

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <p className="max-w-2xl text-muted-foreground text-sm">
          {hosted
            ? 'Replay Claire’s past mistakes through the real pipeline and today’s clinic data, on the separate Harness database. Nothing is sent to anyone.'
            : 'Replay Claire through the real chatbot pipeline in a throwaway database. Nothing is sent to anyone, and nothing touches your real data.'}
        </p>
        {isLoading ? (
          <Skeleton className="h-7 w-64" />
        ) : (
          <div className="flex flex-wrap gap-2">
            {hosted ? (
              <StatusPill ok icon={Database} label="Harness database" />
            ) : (
              <>
                <StatusPill ok icon={Laptop} label="Local only" />
                <StatusPill
                  ok={Boolean(status?.docker)}
                  icon={Container}
                  label={status?.docker ? 'Docker ready' : 'Docker off'}
                />
              </>
            )}
            <StatusPill
              ok={Boolean(status?.openAiKey)}
              icon={KeyRound}
              label={status?.openAiKey ? 'Live AI ready' : 'No OpenAI key'}
            />
          </div>
        )}
      </div>

      {status && !status.available ? (
        <Alert variant="destructive" className="mb-6">
          <Container className="size-4" />
          <AlertTitle>Can’t run yet</AlertTitle>
          <AlertDescription>{status.reason}</AlertDescription>
        </Alert>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
        <Card className="h-fit">
          <CardContent>
            <Tabs defaultValue="scenarios" className="gap-5">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="scenarios" className="gap-1.5">
                  <ShieldCheck className="size-4" />
                  Scenarios
                </TabsTrigger>
                <TabsTrigger value="past" className="gap-1.5">
                  <History className="size-4" />
                  Past mistakes
                </TabsTrigger>
              </TabsList>
              <TabsContent value="scenarios">
                {hosted ? (
                  <p className="rounded-lg border border-dashed p-4 text-muted-foreground text-sm">
                    Scenarios run on a local stack: each one seeds and wipes its
                    own database, which the shared Harness database can’t allow.
                    They also run in CI on every pull request.
                  </p>
                ) : (
                  <ScenariosPanel
                    disabled={disabled}
                    runs={runs}
                    onRun={setSelectedId}
                  />
                )}
              </TabsContent>
              <TabsContent value="past">
                <PastCasesPanel
                  hosted={hosted}
                  packs={status?.packs ?? []}
                  disabled={disabled}
                  liveUnavailableReason={liveUnavailableReason}
                  onRun={setSelectedId}
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <div className="space-y-6">
          {run ? (
            <HarnessRunView run={run} />
          ) : (
            <Empty className="border border-dashed py-16">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <FlaskConical />
                </EmptyMedia>
                <EmptyTitle>Results show up here</EmptyTitle>
                <EmptyDescription>
                  Run a scenario or replay a past mistake. You’ll see Claire’s
                  reply, any drafts the checks blocked, the tools she used, and
                  what would have been sent.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          )}
          <RecentRuns
            runs={runs}
            selectedId={activeId}
            onSelect={setSelectedId}
          />
        </div>
      </div>
    </div>
  );
}

export function HarnessPage({
  initialConversationId,
}: { initialConversationId?: string } = {}) {
  const [tab, setTab] = useState('live');
  const [opened, setOpened] = useState<HarnessConversation | null>(null);
  const { status } = useHarnessStatus();
  const { importConversation } = useImportLiveConversation({
    onSuccess: (conversation) => {
      setOpened(conversation);
      setTab('chat');
    },
  });
  // Real conversations can only be continued where test ones are kept.
  const onContinue =
    status?.mode === 'hosted'
      ? (conversationId: string, upTo: string) =>
          importConversation({ conversationId, upTo })
      : undefined;
  return (
    <div className="container max-w-7xl px-6 py-8 lg:px-8">
      <div className="mb-6 space-y-1">
        <h1 className="flex items-center gap-2 font-bold text-3xl">
          <FlaskConical className="size-7 text-primary" />
          Harness
        </h1>
        <p className="max-w-2xl text-muted-foreground">
          Watch Claire’s real conversations and score each reply against the
          evals, chat with her as a test customer, or replay past mistakes.
        </p>
      </div>
      <Tabs value={tab} onValueChange={setTab} className="gap-6">
        <TabsList>
          <TabsTrigger value="live" className="gap-1.5">
            <Radio className="size-4" />
            Live traffic
          </TabsTrigger>
          <TabsTrigger value="chat" className="gap-1.5">
            <MessageSquarePlus className="size-4" />
            Test chat
          </TabsTrigger>
          <TabsTrigger value="replay" className="gap-1.5">
            <RotateCcw className="size-4" />
            Replay
          </TabsTrigger>
        </TabsList>
        <TabsContent value="live">
          <LiveTrafficPanel
            onContinue={onContinue}
            initialConversationId={initialConversationId}
          />
        </TabsContent>
        <TabsContent value="chat">
          <TestChatWorkspace openConversation={opened} />
        </TabsContent>
        <TabsContent value="replay">
          <ReplayWorkspace />
        </TabsContent>
      </Tabs>
    </div>
  );
}
